// Mobile horizontal chip strip + bottom-sheet drawers for filters.
// State stays in the parent (SearchResults) so the existing filter logic
// keeps working unchanged.

import { ArrowsLeftRight, CaretDown, X } from "@phosphor-icons/react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { formatPrice } from "@/lib/api";

function Pill({ label, count, children, testid }) {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          type="button"
          data-testid={`filter-pill-${testid}`}
          className="shrink-0 snap-start px-4 py-2 rounded-full border border-[#E0DDD6] bg-white text-[#1C1C1C] text-sm font-medium inline-flex items-center gap-1.5 whitespace-nowrap"
        >
          {label}
          {count > 0 && (
            <span
              data-testid={`filter-pill-count-${testid}`}
              className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#C29B62] text-[#0A0A0B] text-[10px] font-bold"
            >
              {count}
            </span>
          )}
          <CaretDown size={12} className="text-[#666460]" />
        </button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        className="bg-[#F7F5F0] rounded-t-2xl border-t border-[#E0DDD6] max-h-[80vh] overflow-y-auto"
        data-testid={`filter-sheet-${testid}`}
      >
        <SheetHeader className="text-left mb-4">
          <SheetTitle className="font-serif text-2xl tracking-tight text-[#1C1C1C]">{label}</SheetTitle>
        </SheetHeader>
        {children}
      </SheetContent>
    </Sheet>
  );
}

export default function MobileFilterPills({
  // Filter state (passed straight from parent)
  stops, setStops,
  airlines, setAirlines, airlineList,
  priceMax, setPriceMax,
  sort, setSort,
  onSwap,
}) {
  const stopsCount = Object.values(stops).filter((v) => v === false).length;
  const airlinesCount = airlineList.reduce(
    (acc, c) => acc + (airlines[c] === false ? 1 : 0),
    0,
  );
  const priceActive = priceMax < 5000 ? 1 : 0;

  return (
    <div
      className="md:hidden flex gap-2 overflow-x-auto no-scrollbar -mx-5 px-5 py-3 snap-x"
      data-testid="mobile-filter-strip"
    >
      <button
        type="button"
        onClick={onSwap}
        aria-label="Swap origin & destination"
        data-testid="filter-pill-swap"
        className="shrink-0 snap-start w-10 h-10 rounded-full border border-[#E0DDD6] bg-white flex items-center justify-center text-[#C29B62]"
      >
        <ArrowsLeftRight size={15} weight="bold" />
      </button>

      <Pill label="Stops" count={stopsCount} testid="stops">
        <div className="space-y-3">
          {[
            { v: 0, l: "Non-stop" },
            { v: 1, l: "1 stop" },
            { v: 2, l: "2+ stops" },
          ].map((o) => (
            <label key={o.v} className="flex items-center gap-3 cursor-pointer py-2">
              <Checkbox
                checked={stops[o.v] !== false}
                onCheckedChange={(v) => setStops({ ...stops, [o.v]: !!v })}
                data-testid={`m-filter-stops-${o.v}`}
              />
              <span className="text-base text-[#1C1C1C]">{o.l}</span>
            </label>
          ))}
        </div>
      </Pill>

      <Pill label="Price" count={priceActive} testid="price">
        <p className="tcp-overline text-[#666460] mb-3">Max price · {formatPrice(priceMax)}</p>
        <Slider
          value={[priceMax]}
          max={5000}
          min={100}
          step={50}
          onValueChange={(v) => setPriceMax(v[0])}
          data-testid="m-filter-price"
        />
        <p className="text-xs text-[#666460] mt-4">Drag to set the maximum you're willing to pay.</p>
      </Pill>

      <Pill label="Airlines" count={airlinesCount} testid="airlines">
        <div className="space-y-2">
          {airlineList.map((code) => (
            <label key={code} className="flex items-center gap-3 cursor-pointer py-2">
              <Checkbox
                checked={airlines[code] !== false}
                onCheckedChange={(v) => setAirlines({ ...airlines, [code]: !!v })}
                data-testid={`m-filter-airline-${code}`}
              />
              <span className="text-base text-[#1C1C1C]">{code}</span>
            </label>
          ))}
        </div>
      </Pill>

      <Pill label="Sort" count={sort !== "price" ? 1 : 0} testid="sort">
        <div className="space-y-2">
          {[
            { v: "price", l: "Price (low to high)" },
            { v: "duration", l: "Fastest" },
            { v: "depart", l: "Earliest departure" },
          ].map((o) => (
            <button
              key={o.v}
              type="button"
              onClick={() => setSort(o.v)}
              data-testid={`m-sort-${o.v}`}
              className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                sort === o.v
                  ? "bg-[#C29B62] border-[#C29B62] text-[#0A0A0B] font-semibold"
                  : "bg-white border-[#E0DDD6] text-[#1C1C1C]"
              }`}
            >
              {o.l}
            </button>
          ))}
        </div>
      </Pill>
    </div>
  );
}
